import React, { useState, useEffect } from 'react';
import { HumanVerificationWidget } from '../../../features/security';
import { IVerifyHumanResponse } from '../../../types/humanVerification';
import { Button, Card, CardHeader, CardBody } from '@heroui/react';

interface AppEntryVerificationProps {
	children: React.ReactNode;
	onVerified: () => void;
}

const AppEntryVerification: React.FC<AppEntryVerificationProps> = ({ children, onVerified }) => {
	const [isVerified, setIsVerified] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Check if user has already been verified in this session
	useEffect(() => {
		const sessionVerified = sessionStorage.getItem('human_verified');
		const lastVerification = sessionStorage.getItem('human_verification_time');

		if (sessionVerified === 'true' && lastVerification) {
			const verificationTime = new Date(lastVerification);
			const now = new Date();
			const timeDiff = now.getTime() - verificationTime.getTime();
			const hoursDiff = timeDiff / (1000 * 3600);

			// Verification valid for 24 hours
			if (hoursDiff < 12) {
				setIsVerified(true);
				onVerified();
				return;
			}
		}
	}, [onVerified]);

	const handleVerified = (token: string, result?: IVerifyHumanResponse) => {
		console.log('Human verification successful:', { token, result });

		// Store verification in session storage
		sessionStorage.setItem('human_verified', 'true');
		sessionStorage.setItem('human_verification_time', new Date().toISOString());

		setIsVerified(true);
		setError(null);
		onVerified();
	};

	const handleError = (error: string) => {
		console.error('Human verification failed:', error);
		setError(error);
		setIsLoading(false);
	};

	const handleRetry = () => {
		setError(null);
		setIsLoading(false);
	};

	// Show main app if already verified
	if (isVerified) {
		return <>{children}</>;
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-linear-to-br p-4">
			<Card className="w-full max-w-md shadow-2xl">
				<CardHeader className="pb-0 pt-6 px-6 flex-col items-center">
					<div className="rounded-full bg-indigo-100 p-3 mb-4">
						<svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
							/>
						</svg>
					</div>
					<h1 className="text-2xl font-bold text-gray-900 text-center">Security Verification</h1>
					<p className="text-sm text-gray-600 text-center mt-2">Please complete the verification to access the application</p>
				</CardHeader>

				<CardBody className="px-6 py-6">
					<div className="space-y-4">
						{/* Verification widget */}
						<div className="flex justify-center">
							<HumanVerificationWidget
								provider="turnstile" // You can change to "recaptcha" if preferred
								siteKey={
									// Try environment variable first, then config, then test key
									window.config.TURNSTILE_SITE_KEY ||
									'1x00000000000000000000AA' // Test key for development
								}
								action="app_entry"
								size="flexible"
								onVerified={handleVerified}
								onError={handleError}
								onLoad={() => setIsLoading(false)}
								className="verification-widget"
							/>
						</div>

						{/* Error display */}
						{error && (
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex justify-between items-start">
									<div>
										<h4 className="text-sm font-medium text-red-900">Verification Failed</h4>
										<p className="text-xs text-red-700 mt-1">{error}</p>
									</div>
									<Button size="sm" variant="flat" color="danger" onPress={handleRetry} className="ml-3">
										Retry
									</Button>
								</div>
							</div>
						)}

						{/* Loading state */}
						{isLoading && (
							<div className="text-center py-4">
								<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
								<p className="text-sm text-gray-600">Verifying...</p>
							</div>
						)}

						{/* Development bypass button */}
						{/* {(import.meta.env.MODE === 'development' || (window as any).config?.ENVIRONMENT_NAME === 'Development') && (
							<div className="text-center pt-4 border-t border-gray-200">
								<p className="text-xs text-gray-500 mb-2">Development Mode Only:</p>
								<Button
									size="sm"
									variant="flat"
									color="warning"
									onPress={() => {
										sessionStorage.setItem('human_verified', 'true');
										sessionStorage.setItem('human_verification_time', new Date().toISOString());
										setIsVerified(true);
										onVerified();
									}}
									className="text-xs"
								>
									Skip Verification (Dev)
								</Button>
							</div>
						)} */}
					</div>
				</CardBody>

				{/* <CardFooter className="pt-0 pb-6 px-6">
					<div className="w-full text-center">
						<p className="text-xs text-gray-500">
							This site is protected by security verification and the{' '}
							<a
								href="https://www.msiltd.com/privacy"
								target="_blank"
								rel="noopener noreferrer"
								className="text-indigo-600 hover:underline"
							>
								Privacy Policy
							</a>{' '}
							applies.
						</p>
					</div>
				</CardFooter> */}
			</Card>
		</div>
	);
};

export default AppEntryVerification;
